import { DraftTextField } from './appBlockEditors'
import type { CustomCodeBlock, CustomCodeBridgeConfig } from './types'

interface Props {
  block: CustomCodeBlock
  onChange: (next: CustomCodeBlock) => void
}

// Comma-separated allowlist <-> string[] helpers for the bridge fields.
function listToText(list: string[] | undefined): string {
  return (list || []).join(', ')
}
function textToList(text: string): string[] | undefined {
  const items = text.split(',').map(s => s.trim()).filter(Boolean)
  return items.length ? items : undefined
}

export function CustomCodeEditor({ block, onChange }: Props) {
  const set = (patch: Partial<CustomCodeBlock>) => onChange({ ...block, ...patch })
  const bridge = block.bridge
  const setBridge = (patch: Partial<CustomCodeBridgeConfig>) =>
    set({ bridge: { ...(bridge || {}), ...patch } })
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
        A custom component: HTML/CSS/JS (or a Claude Design component) in its own
        sandboxed frame that fits its content and uses the page theme. It has no
        access to the page or its data unless you grant it through the page
        bridge below. Prefer a standard block when one fits.
      </p>
      <Field label="Sandbox JS (recommended)">
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={block.sandbox ?? true} onChange={e => set({ sandbox: e.target.checked })} />
          Run JS inside a sandboxed iframe
        </label>
      </Field>
      <Field label="HTML">
        <textarea
          value={block.html ?? ''}
          onChange={e => set({ html: e.target.value })}
          rows={6}
          placeholder="<div class='hello'>Hi</div>"
          style={codeStyle}
        />
      </Field>
      <Field label="CSS (scoped to this block)">
        <textarea
          value={block.css ?? ''}
          onChange={e => set({ css: e.target.value })}
          rows={5}
          placeholder=".hello { color: hotpink; }"
          style={codeStyle}
        />
      </Field>
      <Field label="JS">
        <textarea
          value={block.js ?? ''}
          onChange={e => set({ js: e.target.value })}
          rows={6}
          placeholder="// runs in a sandboxed iframe (no parent access)"
          style={codeStyle}
        />
      </Field>
      <Field label="Libraries (one URL per line — cdnjs, jsdelivr or unpkg)">
        <textarea
          value={(block.libs ?? []).join('\n')}
          onChange={e => set({ libs: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })}
          rows={2}
          placeholder="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js"
          style={codeStyle}
        />
      </Field>
      <Field label="Fixed height (px) — empty fits the content">
        <input
          type="number"
          min={20}
          value={block.height ?? ''}
          onChange={e => set({ height: e.target.value ? Math.max(20, Number(e.target.value)) : undefined })}
          style={{ width: 120 }}
        />
      </Field>
      <Field label="Claude Design component (.dc.html) — runs instead of the HTML above">
        <textarea
          value={block.component?.source ?? ''}
          onChange={e => set({
            component: e.target.value.trim()
              ? { ...(block.component || {}), format: 'dc', source: e.target.value }
              : undefined,
          })}
          rows={5}
          placeholder="Paste a component from Reference_Source/design/<name>/source/<Board>.embed.dc.html"
          style={codeStyle}
        />
      </Field>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 10 }}>
        <Field label="Page bridge (window.twasta)">
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={!!bridge?.enabled}
              onChange={e => setBridge({ enabled: e.target.checked })}
            />
            Expose a capability-scoped page API to the sandboxed code
          </label>
        </Field>
        {bridge?.enabled && (
          <>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
              Only what you allowlist here is reachable. Lists are
              comma-separated names; <code>*</code> grants all. In the iframe:
              {' '}<code>twasta.vars.get/set</code>, <code>twasta.actions.run</code>,
              {' '}<code>twasta.navigate</code>, <code>twasta.data.get</code>,
              {' '}<code>twasta.vars.onChange</code>. On published static sites the
              bridge is read-only (vars baked at deploy).
            </p>
            <Field label="Page variables (read + write)">
              {/* Controlled draft, committed on blur and re-synced from the
                  block — keyed by block id so switching blocks never shows
                  (or blurs into) the previous block's list. */}
              <DraftTextField
                key={`${block.id}:vars`}
                value={listToText(bridge.vars)}
                onCommit={text => setBridge({ vars: textToList(text) })}
                placeholder="selectedId, customerName  (or *)"
                style={inputStyle}
              />
            </Field>
            <Field label="Page actions (runnable)">
              <DraftTextField
                key={`${block.id}:actions`}
                value={listToText(bridge.actions)}
                onCommit={text => setBridge({ actions: textToList(text) })}
                placeholder="saveOrder, refreshList  (or *)"
                style={inputStyle}
              />
            </Field>
            <Field label="Data sources (readable)">
              <DraftTextField
                key={`${block.id}:data`}
                value={listToText(bridge.data)}
                onCommit={text => setBridge({ data: textToList(text) })}
                placeholder="orders, customers  (or *)"
                style={inputStyle}
              />
            </Field>
            <Field label="Navigation">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!bridge.navigate}
                  onChange={e => setBridge({ navigate: e.target.checked })}
                />
                Allow twasta.navigate(objType, objName)
              </label>
            </Field>
          </>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const codeStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  fontFamily: 'ui-monospace, SFMono-Regular, "JetBrains Mono", monospace',
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg)', color: 'var(--color-text)',
  resize: 'vertical',
}
